import axios from "axios";

/**
 * Resolve the backend origin. We prefer `REACT_APP_BACKEND_URL` at build time,
 * but when the bundle is served from a different host (e.g. the app was built
 * with the preview URL and then served via a custom production domain like
 * `luxstaymadeira.pt`), we fall back to `window.location.origin` so that API
 * calls and httpOnly cookies stay SAME-ORIGIN. Without this, cross-site
 * cookies get silently dropped and the admin cannot stay logged in.
 */
function resolveBackendUrl() {
  const envUrl = process.env.REACT_APP_BACKEND_URL || "";
  if (typeof window === "undefined") return envUrl;
  const here = window.location.origin;
  // Emergent preview/prod both route /api to the backend on the same host, so
  // calling the current origin is always correct in a browser context.
  if (!envUrl) return here;
  try {
    const envHost = new URL(envUrl).host;
    if (envHost !== window.location.host) return here;
  } catch {
    return here;
  }
  return envUrl;
}

const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;
export { BACKEND_URL };

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

/**
 * Silent refresh-token flow.
 *
 * Strategy:
 *  1. Access token now lives only 15 minutes (backend). Refresh token lasts 7 days.
 *  2. When any authenticated endpoint returns 401, the interceptor:
 *      a. Tries `POST /auth/refresh` ONCE.
 *      b. If refresh succeeds → retries the original request transparently.
 *      c. If refresh fails → redirects to /login (but never when already on a
 *         public page, to avoid logout-race toast noise).
 *  3. Auth endpoints (`/auth/login`, `/auth/register`, `/auth/refresh` itself)
 *     must bubble up errors to the caller — the refresh logic skips them.
 *  4. Concurrent 401s wait on a single in-flight refresh promise so the
 *     backend only sees one /auth/refresh call per expiry window.
 *  5. If the refresh ultimately fails, protected components receive a safe
 *     empty shape so their `const { data } = await api.get()` destructuring
 *     keeps working instead of crashing with `Cannot read of null`.
 */
const PUBLIC_PATHS = ["/", "/login", "/registo", "/recuperar-password", "/reset-password"];
const SAFE_EMPTY = { items: [], groups: {}, total: 0, authenticated: false };
let refreshInFlight = null;

function isAuthEndpoint(url = "") {
  return /\/auth\/(login|register|refresh)/.test(url);
}

async function doRefresh() {
  // Use a bare axios call so we don't recursively hit our own interceptor.
  return axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status = err?.response?.status;
    const cfg = err?.config || {};
    if (status !== 401 || isAuthEndpoint(cfg.url) || cfg._retry) {
      return Promise.reject(err);
    }
    cfg._retry = true;
    try {
      // One in-flight refresh shared by all concurrent 401s
      if (!refreshInFlight) refreshInFlight = doRefresh().finally(() => { refreshInFlight = null; });
      await refreshInFlight;
      // Retry the original request with fresh cookies
      return api.request(cfg);
    } catch (refreshErr) {
      // Refresh itself failed — redirect to login if we're on a protected page
      const path = typeof window !== "undefined" ? window.location.pathname : "/";
      const onPublic = PUBLIC_PATHS.includes(path);
      if (!onPublic) {
        // setTimeout lets the current render tick finish before nav
        setTimeout(() => { window.location.replace("/login"); }, 50);
      }
      // Swallow to avoid "Request failed with status code 401" toasts
      return Promise.resolve({ data: SAFE_EMPTY, status: 401, silent: true });
    }
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Erro inesperado";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg.replace(/^Value error,\s*/, "") : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    return JSON.stringify(detail);
  }
  return String(detail);
}
